import { isRecord, UnknownRecord } from './shared'

/**
 * How a `gatsbyImageData` object was generated, read back off the object itself.
 *
 * The server sends one candidate per `gatsbyImageData(...)` option set in the repo without saying
 * which is which (`gatsbyHelpers/previewImageData.js`). Picking the right one by fingerprinting
 * both sides means the mapping is derived from the data, so a fragment changing its `quality` or
 * `formats` cannot silently select the wrong image — it just stops matching, and the field keeps
 * its build-time value.
 *
 * Shape: `layout|quality|sourceTypes|fallbackFormat`, e.g. `constrained|90|image/webp|png`.
 */
export const imageDataFingerprint = (imageData: unknown): string | undefined => {
  if (!isRecord(imageData) || typeof imageData.layout !== 'string') return undefined

  const images = imageData.images
  if (!isRecord(images)) return undefined

  const fallback = images.fallback
  if (!isRecord(fallback) || typeof fallback.src !== 'string') return undefined

  const params = fallback.src.split('?')[1]
  if (!params) return undefined

  const search = new URLSearchParams(params)
  const quality = search.get('q')
  const fallbackFormat = search.get('fm')
  if (!quality) return undefined

  const sourceTypes = Array.isArray(images.sources)
    ? images.sources
        .map(source => (isRecord(source) && typeof source.type === 'string' ? source.type : ''))
        .filter(Boolean)
        .sort()
        .join(',')
    : ''

  return `${imageData.layout}|${quality}|${sourceTypes}|${fallbackFormat || ''}`
}

/**
 * The option set used by `ContentfulAssetFluidFragment`, for a live rich-text embed that has no
 * build-time image to match against. Kept in sync with `src/graphql/modules/contentfulAsset.ts`
 * by `src/__tests__/helpers/imageDataVariant.ts`, which asserts a real built object fingerprints
 * to exactly this.
 */
export const DEFAULT_IMAGE_DATA_FINGERPRINT = 'constrained|90|image/webp|png'

const candidateList = (candidates: unknown): unknown[] => (Array.isArray(candidates) ? candidates : [])

/**
 * The candidate generated with the same options as `buildTimeImageData`.
 *
 * Returns undefined when nothing matches, which the caller must treat as "keep the build-time
 * image". Showing an image built with the wrong quality or format set would be a silent lie about
 * what publishing will produce, and that is worse than not updating.
 */
export const selectImageDataVariant = (candidates: unknown, buildTimeImageData: unknown): UnknownRecord | undefined => {
  const wanted = imageDataFingerprint(buildTimeImageData) || DEFAULT_IMAGE_DATA_FINGERPRINT

  const match = candidateList(candidates).find(candidate => imageDataFingerprint(candidate) === wanted)

  return isRecord(match) ? match : undefined
}
